/**
 * Sensor Fusion Lean Angle Calculator
 *
 * Combines accelerometer, gyroscope, and GPS data for the most accurate
 * lean angle calculation using a complementary filter approach.
 *
 * Data source hierarchy:
 * 1. Gyroscope - High-frequency response, captures quick transitions
 * 2. Accelerometer - Absolute gravity reference, corrects gyro drift
 * 3. GPS - Physics-based validation, detects systematic sensor bias
 *
 * Algorithm:
 * - Complementary filter fuses gyro + accelerometer
 * - GPS-derived lean provides sanity check and bias correction
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { LeanAnglePoint } from '../domain/telemetry/processed/LeanAngle'
import { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import { GyroscopeReading } from '../domain/telemetry/raw/GyroscopeReading'
import {
  calculateSamplingFrequencyHz,
  getFrequencyAdaptiveConfig,
} from '../utils/timeUtils'
import { smoothGpsCoordinates } from '../utils/smoothingUtils'
import { calculateDistance } from '../utils/distanceUtils'
import {
  estimateSensorFrameCalibration,
  applySensorFrameCalibration,
} from './sensorFrameCalibration'
import {
  classifyVehicleLeanRegime,
  computeGpsCoordinatedLeanSeries,
} from './gpsCoordinatedLean'
import { reduceReadingsToGpsTicks } from './imuWindowAverage'
import { estimateGyroLeanRateBiasDegS, leanRateDegS } from './gyroBias'
import { KMH_PER_MPS } from '../utils/speedUnits'
import { MS_PER_S } from '../utils/timeUnits'

interface SensorFusionConfig {
  /**
   * Complementary filter alpha (0-1)
   * Higher = trust gyroscope more (faster response, more drift)
   * Lower = trust accelerometer more (less drift, more noise)
   * Typical: 0.96-0.98 for motorcycle telemetry
   */
  alpha: number

  /**
   * Weight for GPS validation (0-1)
   * When sensor and GPS angles diverge significantly, blend GPS estimate
   * 0 = ignore GPS entirely, 1 = trust GPS as much as sensors
   */
  gpsValidationWeight: number

  /**
   * Threshold (degrees) for GPS correction
   * Only apply GPS correction if sensor vs GPS difference exceeds this
   */
  gpsCorrectThreshold: number

  /**
   * Minimum speed (km/h) for GPS lean calculation
   * Below this, GPS-derived lean is unreliable
   */
  minSpeedForGps: number

  /**
   * Constant lean-rate offset of the calibrated gyro, deg/s (left
   * positive), removed before integration. Measured from the streams when
   * absent; the session processor supplies the session's value.
   */
  leanRateBiasDegS?: number
}

const DEFAULT_CONFIG: SensorFusionConfig = {
  alpha: 0.97,
  gpsValidationWeight: 0.15,
  gpsCorrectThreshold: 8, // degrees
  minSpeedForGps: 15, // km/h
}

function calculateTurnRadius(
  p1: GpsPoint,
  p2: GpsPoint,
  p3: GpsPoint
): number | null {
  const d1 = calculateDistance(
    p1.latitude,
    p1.longitude,
    p2.latitude,
    p2.longitude
  )
  const d2 = calculateDistance(
    p2.latitude,
    p2.longitude,
    p3.latitude,
    p3.longitude
  )
  const d3 = calculateDistance(
    p1.latitude,
    p1.longitude,
    p3.latitude,
    p3.longitude
  )

  if (d1 < 2 || d2 < 2 || d3 < 2) return null

  const s = (d1 + d2 + d3) / 2
  const areaSquared = s * (s - d1) * (s - d2) * (s - d3)

  if (areaSquared <= 0.1) return null

  const area = Math.sqrt(areaSquared)
  const radius = (d1 * d2 * d3) / (4 * area)

  if (radius < 5 || radius > 10000) return null

  return radius
}

function calculateTurnDirection(
  p1: GpsPoint,
  p2: GpsPoint,
  p3: GpsPoint
): number {
  const heading1 = Math.atan2(
    p2.latitude - p1.latitude,
    p2.longitude - p1.longitude
  )
  const heading2 = Math.atan2(
    p3.latitude - p2.latitude,
    p3.longitude - p2.longitude
  )

  let headingChange = heading2 - heading1
  while (headingChange > Math.PI) headingChange -= 2 * Math.PI
  while (headingChange < -Math.PI) headingChange += 2 * Math.PI

  return headingChange >= 0 ? 1 : -1
}

function getAccelLeanAngle(reading: AccelerometerReading): number {
  const factor = reading.unit === 'g' ? 9.81 : 1
  const ax = reading.x * factor
  const ay = reading.y * factor
  const az = reading.z * factor

  // Roll angle from gravity vector
  const roll = Math.atan2(ay, Math.sqrt(ax * ax + az * az))
  return (roll * 180) / Math.PI
}

/**
 * Calculate lean angle using sensor fusion (accelerometer + gyroscope + GPS)
 *
 * Uses complementary filter to combine:
 * - Gyroscope for fast response (integrated with drift)
 * - Accelerometer for absolute reference (corrects drift)
 * - GPS for physics-based validation (detects bias)
 */
export function calculateLeanAngleWithSensorFusion(
  gpsPoints: GpsPoint[],
  accelerometer: AccelerometerReading[],
  gyroscope: GyroscopeReading[],
  config: Partial<SensorFusionConfig> = {}
): LeanAnglePoint[] {
  const cfg = { ...DEFAULT_CONFIG, ...config }

  if (gpsPoints.length < 10) {
    return gpsPoints.map((point) => ({
      leanAngleDegrees: 0,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      speedKmh: point.speedKmh,
      source: 'combined' as const,
    }))
  }

  // Normalize sensor streams into the bike frame (x fwd, y right, z down,
  // at-rest accel (0,0,−1)g) with the same calibration the acceleration
  // calculator uses — mounts are arbitrary (the field unit is z-up, tilted
  // ~33° and yawed ~160°) and every formula below assumes the bike frame.
  // An untrusted calibration is the identity: data passes through as before.
  const calibration = estimateSensorFrameCalibration(
    accelerometer,
    gpsPoints,
    gyroscope
  )
  // One reading per GPS tick — the window average, never a nearest-sample
  // pick, so a full-rate (GoPro 200 Hz) stream does not alias engine
  // vibration into the roll reference and the integrated roll rate.
  const calibratedAccelerometer = reduceReadingsToGpsTicks(
    applySensorFrameCalibration(accelerometer, calibration),
    gpsPoints
  )
  const calibratedGyroscope = reduceReadingsToGpsTicks(
    applySensorFrameCalibration(gyroscope, calibration),
    gpsPoints
  )

  // Leaning-vehicle gate for the complementary reference. The accelerometer
  // gravity-roll reference is blind in a COORDINATED turn (the net specific
  // force stays along chassis-z, so ay ≈ 0 regardless of lean): it drags the
  // fused lean toward 0 at (1−alpha)/step while the step-5 dead band only
  // recovers to ~gpsCorrectThreshold below the true lean — a systematic
  // ~22% under-read measured on session-143033. For sessions classified as
  // 'leaning' (physics gate on calibrated raw ay vs GPS lateral — never the
  // fused output), the GPS turn-radius lean replaces the gravity roll as the
  // low-pass reference wherever it is defined. 'upright' / 'unknown'
  // sessions keep the gravity reference — cars and ungateable data are
  // untouched.
  const gpsCoordinatedLean = computeGpsCoordinatedLeanSeries(gpsPoints)
  const regime = classifyVehicleLeanRegime(
    gpsPoints,
    calibratedAccelerometer.filter(
      (reading): reading is AccelerometerReading => reading !== undefined
    ),
    gpsCoordinatedLean
  )
  const leanRateBiasDegS =
    cfg.leanRateBiasDegS ??
    estimateGyroLeanRateBiasDegS(
      gpsPoints,
      calibratedGyroscope,
      gpsCoordinatedLean
    ) ??
    0

  // Prepare GPS data for physics-based lean calculation
  const frequencyHz = calculateSamplingFrequencyHz(gpsPoints)
  const frequencyConfig = getFrequencyAdaptiveConfig(frequencyHz)
  const gpsSmoothing = Math.round(15 * frequencyConfig.frequencyScale)
  const smoothedGps = smoothGpsCoordinates(gpsPoints, gpsSmoothing)
  const lookAhead = Math.max(3, Math.ceil(frequencyHz / 3))

  // Fusion state
  let fusedAngle = 0
  let lastTimestamp: Date | null = null

  const results: LeanAnglePoint[] = []

  for (let i = 0; i < gpsPoints.length; i++) {
    const point = gpsPoints[i]
    const accelReading = calibratedAccelerometer[i]
    const gyroReading = calibratedGyroscope[i]

    // Calculate time delta for gyro integration
    const dt = lastTimestamp
      ? (point.timestamp.getTime() - lastTimestamp.getTime()) / MS_PER_S
      : 0
    lastTimestamp = point.timestamp

    // Step 1: Get accelerometer-derived lean angle (absolute reference)
    let accelAngle = 0
    if (accelReading) {
      accelAngle = getAccelLeanAngle(accelReading)
    }

    // Step 2: Integrate gyroscope (high-frequency updates)
    let gyroAngle = fusedAngle // Start from previous fused angle
    if (gyroReading && dt > 0 && dt < 0.5) {
      // Sanity check on dt
      const rollRate = leanRateDegS(gyroReading) - leanRateBiasDegS
      gyroAngle = fusedAngle + rollRate * dt
    }

    // GPS turn-radius lean (computed before the complementary step so the
    // leaning-regime path can use it as the low-pass reference; identical
    // result to computing it afterwards — it reads nothing the filter
    // mutates).
    let gpsLeanAngle: number | null = null
    if (
      point.speedKmh >= cfg.minSpeedForGps &&
      i >= lookAhead &&
      i < smoothedGps.length - lookAhead
    ) {
      const radius = calculateTurnRadius(
        smoothedGps[i - lookAhead],
        smoothedGps[i],
        smoothedGps[i + lookAhead]
      )

      if (radius !== null) {
        const speedMs = point.speedKmh / KMH_PER_MPS
        const centripetalAccel = (speedMs * speedMs) / radius

        if (centripetalAccel <= 25) {
          // Realistic limit
          const leanRad = Math.atan(centripetalAccel / 9.81)
          const leanMag = (leanRad * 180) / Math.PI
          const direction = calculateTurnDirection(
            smoothedGps[i - 1],
            smoothedGps[i],
            smoothedGps[i + 1]
          )
          gpsLeanAngle = direction * leanMag
        }
      }
    }

    // Step 3: Complementary filter. For leaning sessions the low-pass
    // reference is the GPS turn-radius lean (the gravity roll is blind in a
    // coordinated turn); the gravity roll still covers straights / low
    // speed / GPS-degenerate points, and remains THE reference for upright
    // and unclassified sessions.
    const lowPassReference =
      regime === 'leaning' && gpsLeanAngle !== null ? gpsLeanAngle : accelAngle
    if (accelReading && gyroReading && dt > 0) {
      // Classic complementary filter
      fusedAngle = cfg.alpha * gyroAngle + (1 - cfg.alpha) * lowPassReference
    } else if (accelReading) {
      // No gyro, use the reference directly
      fusedAngle = lowPassReference
    } else if (gyroReading && dt > 0) {
      // No accelerometer, use integrated gyro (will drift!)
      fusedAngle = gyroAngle
    }
    // else: keep previous fusedAngle

    // Step 4: Apply GPS correction if deviation is significant. The dead
    // band's noise-rejection purpose is unchanged: inside gpsCorrectThreshold
    // the filter does not chase GPS jitter with the aggressive 15% snap. For
    // leaning sessions the reference switch above supplements it (a gentle
    // (1−alpha)/step pull toward the same physics), so the corner
    // equilibrium sits AT the GPS lean instead of a dead-band-width below
    // it.
    if (gpsLeanAngle !== null) {
      const deviation = Math.abs(fusedAngle - gpsLeanAngle)
      if (deviation > cfg.gpsCorrectThreshold) {
        // Blend GPS estimate to correct sensor bias
        fusedAngle =
          (1 - cfg.gpsValidationWeight) * fusedAngle +
          cfg.gpsValidationWeight * gpsLeanAngle
      }
    }

    // Clamp to realistic range
    fusedAngle = Math.max(-65, Math.min(65, fusedAngle))

    results.push({
      leanAngleDegrees: Math.round(fusedAngle * 100) / 100,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      speedKmh: point.speedKmh,
      source: 'combined' as const,
    })
  }

  return results
}
